<template>
  <div>
    <Navbar />
    <div class="container">
      <section class="portfolio-hero">
        <p class="hero-kicker">Investment Portfolio</p>
        <h1>Recent Acquisitions</h1>
        <p class="hero-subtitle">A live snapshot of acquisitions, valuations, and exits across our client portfolio.</p>
      </section>

      <section class="numbers-section" ref="propertyListTop">
        <div class="numbers-shell">
          <div class="numbers-left">
            <p class="numbers-kicker">TRACK RECORD ACROSS {{ displayPropertyCount }} PORTFOLIOS</p>
            <h2 class="numbers-title">Let the Results<br><span>Speak</span></h2>

            <div class="numbers-grid">
              <article class="metric-card">
                <p>TOTAL PROPERTIES</p>
                <h3>{{ numbersStats.totalProperties }}</h3>
              </article>
              <article class="metric-card">
                <p>AVG PURCHASE PRICE</p>
                <h3>{{ formatStatMoney(numbersStats.avgPurchasePrice) }}</h3>
              </article>
              <article class="metric-card">
                <p>MEDIAN RENTAL YIELD</p>
                <h3>{{ formatStatNumber(numbersStats.medianAnnualRentalYield, 2) }}%</h3>
              </article>
              <article class="metric-card">
                <p>MEDIAN ANNUAL GROWTH</p>
                <h3>{{ formatStatNumber(numbersStats.medianAnnualCapitalGainRate, 2) }}%</h3>
              </article>
            </div>

            <div class="chart-disclaimer chart-disclaimer-left">
              <p class="chart-disclaimer-heading">Important — historical data, not a forecast</p>
              <p class="chart-disclaimer-body">Figures shown are historical transaction results for individual past Norland Advisory clients. &ldquo;Current value&rdquo; is an internal estimate, not a formal valuation. Yield calculations exclude loan interest, land tax, council rates, insurance,
                depreciation, maintenance, vacancy, and personal tax. The reference line on the chart is illustrative only &mdash; it is not a regression, trend fit, or forecast. Past performance is not a reliable indicator of future results; property prices can fall as well as rise. Nothing on this
                page is personal financial advice. <router-link to="/disclaimer">Read the full disclaimer.</router-link></p>
            </div>
          </div>

          <div class="numbers-right">
            <ScatterChart :series="numbersStats.capitalGainVsTenure" />
          </div>
        </div>
      </section>

      <section class="portfolio-toolbar">
        <div>
          <p class="toolbar-kicker">Portfolio Snapshot</p>
          <p class="result-count">Showing {{ displayStart }}-{{ displayEnd }} of {{ totalProperties }} properties</p>
        </div>

        <label class="sort-control">
          <span>Sort By</span>
          <select v-model="sortBy">
            <option value="recent">Most Recent</option>
            <option value="price-desc">Highest Purchase Price</option>
            <option value="price-asc">Lowest Purchase Price</option>
            <option value="gain-desc">Highest Capital Gain</option>
            <option value="annualized-desc">Highest Annualized Growth</option>
            <option value="yield-desc">Highest Rental Yield</option>
          </select>
        </label>
      </section>

      <section class="acquisition-grid">
        <article v-for="property in paginatedProperties" :key="property.id" class="acquisition-card">
          <div class="card-image-wrap">
            <img :src="property.image" :alt="`${property.location} property image`" loading="lazy" />
            <span v-if="property.isSold" class="sold-tag">Sold</span>
          </div>

          <div class="card-main">
            <h3 class="card-location">{{ property.location }}</h3>

            <dl class="info-list">
              <div class="info-row">
                <dt>Purchase Time</dt>
                <dd>{{ formatDate(property.settlementDate) }}</dd>
              </div>
              <div class="info-row">
                <dt>Purchase Price</dt>
                <dd>{{ formatMoney(property.purchasePrice) }}</dd>
              </div>
              <div class="info-row">
                <dt>Rent</dt>
                <dd>{{ formatMoneyFull(property.rentPw) }}/wk</dd>
              </div>
              <div class="info-row">
                <dt>Rental Yield</dt>
                <dd>{{ formatPercent(property.rentalYield, 2) }}</dd>
              </div>
              <div class="info-row">
                <dt>Land Size</dt>
                <dd>{{ formatLandSize(property.landSize) }}</dd>
              </div>
              <div class="info-row">
                <dt>{{ property.isSold ? 'Sold Price' : 'Valuation' }}</dt>
                <dd>{{ formatMoney(property.isSold ? property.soldPrice : property.valuation) }}</dd>
              </div>
              <div class="info-row">
                <dt>{{ property.isSold ? 'Sold Date' : 'Valuation Date' }}</dt>
                <dd>{{ formatDate(property.isSold ? property.soldDate : property.valuationDate) }}</dd>
              </div>
              <div class="info-row">
                <dt>Capital Gain</dt>
                <dd class="gold">{{ formatMoney(property.capitalGain) }}</dd>
              </div>
              <div class="info-row info-row-emphasis">
                <dt>Annual Growth*</dt>
                <dd>{{ formatAnnualizedGrowth(property.annualizedGrowth) }}</dd>
              </div>
            </dl>
          </div>
        </article>
      </section>

      <p class="growth-disclaimer">*Capital growth in short period from settlement date, or acquisitors below market price may result in an extremely high annualized growth value. The value will become normal in the next few months.</p>

      <section class="pagination-wrap" v-if="totalProperties > 0">
        <el-pagination background layout="prev, pager, next" :current-page="currentPage" :page-size="pageSize" :total="totalProperties" @current-change="handlePageChange" :hide-on-single-page="true" />
      </section>
    </div>
    <SiteFooter />
  </div>
</template>

<script>
import SiteFooter from '@/components/layout/SiteFooter.vue'
import Navbar from '@/components/layout/Navbar.vue'
import ScatterChart from '@/components/ScatterChart.vue'
import { pubStoreSpecAction } from '@/api/apiService'
import env from '@/api/env'

const storeId = env.storeId
const PORTFOLIO_FETCH_LIMIT = 1000

export default {
  components: { Navbar, SiteFooter, ScatterChart },
  data() {
    return {
      propertyAction: 'pubLstProperty',
      currentPage: 1,
      pageSize: 6,
      sortBy: 'recent',
      properties: [],
      totalProperties: 0,
      numbersStats: {
        propertyCount: 200,
        avgPurchasePrice: 720117,
        medianAnnualRentalYield: 5.74,
        medianAnnualCapitalGainRate: 13.46,
        capitalGainVsTenure: [
          2, 4, 6, 8, 10, 12, 14, 16,
          18, 20, 22, 25, 28, 31, 34, 37,
          40, 44, 48, 52, 56, 60, 64, 68
        ]
      }
    }
  },
  created() {
    this.initPropertyApiConfig()
    this.fetchTotalStats()
    this.fetchProperties()
  },
  computed: {
    displayPropertyCount() {
      const count = Math.max(0, Math.round(this.toNumber(this.numbersStats.propertyCount, 0)))
      return count > 0 ? `${count}+` : '0'
    },
    sortedProperties() {
      const list = this.properties.slice()

      switch(this.sortBy) {
        case 'price-desc':
          return list.sort((left, right) => right.purchasePrice - left.purchasePrice)
        case 'price-asc':
          return list.sort((left, right) => left.purchasePrice - right.purchasePrice)
        case 'gain-desc':
          return list.sort((left, right) => right.capitalGain - left.capitalGain)
        case 'annualized-desc':
          return list.sort((left, right) => right.annualizedGrowth - left.annualizedGrowth)
        case 'yield-desc':
          return list.sort((left, right) => right.rentalYield - left.rentalYield)
        case 'recent':
        default:
          return list.sort((left, right) => this.getDateValue(right.settlementDate) - this.getDateValue(left.settlementDate))
      }
    },
    paginatedProperties() {
      // Backend now returns current page lst; avoid slicing again on frontend.
      return this.sortedProperties
    },
    displayStart() {
      if(!this.paginatedProperties.length || this.totalProperties <= 0) {
        return 0
      }

      return (this.currentPage - 1) * this.pageSize + 1
    },
    displayEnd() {
      if(!this.paginatedProperties.length || this.totalProperties <= 0) {
        return 0
      }

      return this.displayStart + this.paginatedProperties.length - 1
    }
  },
  watch: {
    sortBy() {
      this.currentPage = 1
      this.fetchProperties()
    }
  },
  methods: {
    initPropertyApiConfig() {
      const query = this.$route && this.$route.query ? this.$route.query : {}
      if(query.action) {
        this.propertyAction = query.action
      }
    },
    async fetchProperties() {
      try {
        const offset = this.currentPage * this.pageSize - this.pageSize
        const ret = await pubStoreSpecAction(storeId, 'pubLstProperty', {
          offset,
          limit: this.pageSize,
          sortBy: this.sortBy
        })
        const propertyList = Array.isArray(ret && ret.lst) ? ret.lst : []
        this.totalProperties = this.toNumber(ret && ret.total, this.totalProperties)
        this.properties = propertyList.map((item, index) => this.normalizeProperty(item, index))
        const maxPage = this.totalProperties > 0 ? Math.ceil(this.totalProperties / this.pageSize) : 1
        if(this.currentPage > maxPage) {
          this.currentPage = 1
        }
      } catch(error) {
        console.error('Failed to load property list by pubStoreSpecAction:', error)
      }
    },
    async fetchTotalStats() {
      try {
        const ret = await pubStoreSpecAction(storeId, 'pubGetTotalStats', {})
        this.numbersStats = this.normalizeTotalStats(ret)
      } catch(error) {
        console.error('Failed to load total stats by pubStoreSpecAction:', error)
      }
    },
    normalizeTotalStats(source) {
      const raw = source && typeof source === 'object'
        ? (source.data && typeof source.data === 'object' ? source.data : source)
        : {}

      return {
        propertyCount: this.toNumber(this.pick(raw, ['propertyCount', 'totalProperties', 'totalPropertyCount', 'portfolioCount']), this.numbersStats.propertyCount),
        avgPurchasePrice: this.toNumber(this.pick(raw, ['avgPurchasePrice', 'avg_purchase_price', 'purchasePriceAvg']), this.numbersStats.avgPurchasePrice) / 100,
        medianAnnualRentalYield: this.toNumber(this.pick(raw, ['medianAnnualRentalYield']), this.numbersStats.medianAnnualRentalYield),
        medianAnnualCapitalGainRate: this.toNumber(this.pick(raw, ['medianAnnualCapitalGainRate']), this.numbersStats.medianAnnualCapitalGainRate),
        totalProperties: this.toNumber(this.pick(raw, ['totalProperties', 'total_properties', 'propertyCount']), this.numbersStats.totalProperties),
        capitalGainVsTenure: this.normalizeGainSeries(this.pick(raw, ['accumulatedCapitalGainVsTenurePeriod']), this.numbersStats.capitalGainVsTenure)
      }
    },
    normalizeGainSeries(source, fallback = []) {
      const list = Array.isArray(source)
        ? source
        : (source && typeof source === 'object'
          ? Object.entries(source).map(([key, value]) => ({ tenure: key, gain: value }))
          : []);

      const normalized = list
        .map((item, index) => {
          if(typeof item === 'number' || typeof item === 'string') {
            return {
              x: index + 1,
              y: this.toNumber(item, NaN)
            };
          }

          if(item && typeof item === 'object') {
            const x = item.holdingYears;
            const y = item.percentage;
            return { x, y, prop: item.prop };
          }

          return { x: index + 1, y: NaN };
        })
        .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y));

      if(normalized.length) {
        return normalized;
      }

      if(Array.isArray(fallback)) {
        return fallback
          .map((value, index) => ({ x: index + 1, y: this.toNumber(value, NaN), prop: value.prop }))
          .filter((point) => Number.isFinite(point.y));
      }

      return [];
    },
    normalizeProperty(item, index) {
      const source = item && typeof item === 'object' ? item : {}
      const purchasePrice = this.toNumber(this.pick(source, ['purchasePrice', 'purchase_price', 'price', 'buyPrice']))
      const settlementDate = this.pick(source, ['settlementDate', 'settleDate', 'purchaseDate', 'purchase_time', 'buyDate', 'createdAt'])
      const rentPw = this.toNumber(this.pick(source, ['rentPw', 'rent', 'weeklyRent', 'rent_per_week']))
      const landSize = this.toNumber(this.pick(source, ['landSize', 'land_size', 'size']))
      const valuation = this.toNumber(this.pick(source, ['valuation', 'currentValue', 'marketValue', 'estimateValue']))
      const soldPrice = this.toNumber(this.pick(source, ['soldPrice', 'sold_price', 'salePrice']))
      const valuationDate = this.pick(source, ['valuationDate', 'updatedAt', 'currentValueDate'])
      const soldDate = this.pick(source, ['soldDate', 'saleDate', 'sold_at'])
      const mappedCapitalGain = this.toNumber(this.pick(source, ['capitalGain', 'capital_gain', 'gain']))
      const mappedAnnualizedGrowth = this.toNumber(this.pick(source, ['annualizedGrowth', 'annualGrowth', 'growth', 'annual_growth']), NaN)
      const status = String(this.pick(source, ['status', 'propertyStatus']) || '').toLowerCase()
      const isSold = Boolean(soldPrice || soldDate || status === 'sold')
      const exitValue = isSold ? (soldPrice || valuation) : valuation
      const exitDate = isSold ? (soldDate || valuationDate) : valuationDate
      const computedCapitalGain = exitValue > 0 && purchasePrice > 0 ? Math.max(exitValue - purchasePrice, 0) : 0
      const computedRentalYield = purchasePrice > 0 && rentPw > 0 ? (rentPw * 52 * 100) / purchasePrice : NaN
      const annualizedGrowth = Number.isFinite(mappedAnnualizedGrowth)
        ? mappedAnnualizedGrowth
        : this.computeAnnualizedGrowth(purchasePrice, exitValue, settlementDate, exitDate)

      return {
        id: this.pick(source, ['id', 'propertyId', '_id', 'propIdNo']) || index + 1,
        location: this.pick(source, ['location', 'suburb', 'area', 'city', 'address']) || 'N/A',
        settlementDate,
        purchasePrice,
        rentPw,
        rentalYield: Number.isFinite(computedRentalYield) ? computedRentalYield : this.toNumber(this.pick(source, ['rentalYield', 'yield', 'rentYield']), NaN),
        landSize,
        valuation,
        valuationDate,
        soldPrice,
        soldDate,
        isSold,
        capitalGain: mappedCapitalGain || computedCapitalGain,
        annualizedGrowth,
        image: this.pick(source, ['image', 'cover', 'coverImage', 'img']) || 'https://images.unsplash.com/photo-1494526585095-c41746248156?auto=format&fit=crop&w=1400&q=80'
      }
    },
    computeAnnualizedGrowth(purchasePrice, exitValue, startDate, endDate) {
      if(!(purchasePrice > 0) || !(exitValue > 0)) {
        return 0
      }

      const start = this.parseDate(startDate)
      const end = this.parseDate(endDate)
      if(!start || !end || end <= start) {
        return ((exitValue - purchasePrice) / purchasePrice) * 100
      }

      const years = (end.getTime() - start.getTime()) / (365.25 * 24 * 60 * 60 * 1000)
      if(!(years > 0)) {
        return 0
      }

      return (Math.pow(exitValue / purchasePrice, 1 / years) - 1) * 100
    },
    parseDate(input) {
      if(!input) {
        return null
      }

      const date = new Date(input)
      return Number.isNaN(date.getTime()) ? null : date
    },
    getDateValue(input) {
      const date = this.parseDate(input)
      return date ? date.getTime() : 0
    },
    pick(source, keys) {
      if(!source || typeof source !== 'object') {
        return undefined
      }

      for(let i = 0; i < keys.length; i += 1) {
        const value = source[keys[i]]
        if(value !== undefined && value !== null && value !== '') {
          return value
        }
      }

      return undefined
    },
    toNumber(value, fallback = 0) {
      if(typeof value === 'number') {
        return Number.isFinite(value) ? value : fallback
      }

      if(typeof value === 'string') {
        const normalized = value.replace(/[^\d.-]/g, '')
        const parsed = Number(normalized)
        return Number.isFinite(parsed) ? parsed : fallback
      }

      return fallback
    },
    formatDate(input) {
      const date = this.parseDate(input)
      if(!date) {
        return '-'
      }

      return date.toLocaleDateString('en-AU', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
    },
    formatMoney(value) {
      const num = this.toNumber(value) / 100
      if(num >= 1000000) {
        return `$${(num / 1000000).toFixed(2)}m`
      }
      if(num >= 1000) {
        return `$${Math.round(num / 1000)}k`
      }
      return `$${Math.round(num)}`
    },
    formatMoneyFull(value) {
      const num = this.toNumber(value) / 100
      return `$${Math.round(num).toLocaleString('en-AU')}`
    },
    formatPercent(value, decimals = 2) {
      const num = this.toNumber(value, NaN)
      if(!Number.isFinite(num)) {
        return '-'
      }

      return `${num.toLocaleString('en-AU', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      })}%`
    },
    formatAnnualizedGrowth(value) {
      const num = this.toNumber(value, NaN)
      if(!Number.isFinite(num) || num > 200) {
        return '-'
      }

      return this.formatPercent(num, 2)
    },
    formatLandSize(value) {
      const num = this.toNumber(value, NaN)
      if(!Number.isFinite(num) || num <= 0) {
        return '-'
      }

      return `${Math.round(num).toLocaleString('en-AU')} sqm`
    },
    formatStatMoney(value) {
      const num = this.toNumber(value, 0)
      return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        maximumFractionDigits: 0
      }).format(num)
    },
    formatStatNumber(value, decimals = 2) {
      const num = this.toNumber(value, 0)
      return num.toLocaleString('en-AU', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      })
    },
    scrollToPropertyListTop() {
      this.$nextTick(() => {
        if(this.$refs.propertyListTop && typeof this.$refs.propertyListTop.scrollIntoView === 'function') {
          this.$refs.propertyListTop.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    },
    async handlePageChange(page) {
      this.currentPage = page
      await this.fetchProperties()
      this.scrollToPropertyListTop()
    }
  }
}
</script>

<style scoped>
.container {
  --pf-bg: #f5f3ef;
  --pf-card: #fcfaf7;
  --pf-soft: #f3ede5;
  --pf-navy: #5d544b;
  --pf-text: #6b6259;
  --pf-muted: #8c8379;
  --pf-gold: #c1ab8f;
  --pf-gold-deep: #aa8f6d;
  --pf-line: #ddd6cd;
  --pf-dark: #3c352f;
  padding: 108px 4% 88px;
  background:
    radial-gradient(circle at 16% 4%, rgba(201, 155, 94, 0.18), transparent 34%),
    radial-gradient(circle at 92% 88%, rgba(146, 136, 124, 0.12), transparent 24%),
    var(--pf-bg);
}

.portfolio-hero {
  max-width: 1280px;
  margin: 0 auto;
  text-align: center;
  padding: 72px 0 38px;
}

.hero-kicker {
  margin: 0;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: var(--pf-gold);
  font-size: 0.82rem;
}

.portfolio-hero h1 {
  margin: 16px 0 10px;
  color: var(--pf-navy);
  font-size: clamp(2.2rem, 5.3vw, 4.5rem);
  line-height: 1.02;
  font-weight: 500;
}

.hero-subtitle {
  max-width: 760px;
  margin: 0 auto;
  color: var(--pf-muted);
  font-size: clamp(1rem, 1.6vw, 1.35rem);
  line-height: 1.7;
}

.numbers-section {
  max-width: 1280px;
  margin: 0 auto 42px;
}

.numbers-shell {
  display: grid;
  grid-template-columns: minmax(320px, 43%) minmax(0, 57%);
  gap: 44px;
  padding: 28px;
  border: 1px solid var(--pf-line);
  border-radius: 28px;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.88), rgba(248, 243, 236, 0.96));
  box-shadow: 0 18px 40px rgba(56, 45, 31, 0.08);
}

.numbers-kicker {
  margin: 0;
  color: var(--pf-gold-deep);
  letter-spacing: 0.16em;
  text-transform: uppercase;
  font-size: 0.8rem;
}

.numbers-title {
  margin: 14px 0 18px;
  color: var(--pf-dark);
  font-size: clamp(2rem, 4vw, 3.6rem);
  line-height: 0.96;
}

.numbers-title span {
  color: var(--pf-gold-deep);
}

.numbers-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.metric-card {
  padding: 16px 18px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.84);
  border: 1px solid rgba(221, 214, 205, 0.9);
}

.metric-card p {
  margin: 0;
  color: var(--pf-muted);
  font-size: 0.76rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.metric-card h3 {
  margin: 6px 0 0;
  color: var(--pf-dark);
  font-size: clamp(1.3rem, 2vw, 2rem);
}

.numbers-right {
  display: grid;
  align-content: start;
}

.chart-disclaimer-left {
  margin-top: 18px;
}

.numbers-note {
  margin: 12px 0 0;
  color: var(--pf-muted);
  font-size: 0.88rem;
}

.chart-disclaimer {
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid #c8ddd7;
  background: #eef6f3;
}

.chart-disclaimer-heading {
  margin: 0 0 6px;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #1e5f53;
}

.chart-disclaimer-body {
  margin: 0;
  font-size: 0.76rem;
  line-height: 1.6;
  color: #5e7972;
}

.portfolio-toolbar {
  max-width: 1280px;
  margin: 0 auto 16px;
  display: flex;
  justify-content: space-between;
  align-items: end;
  gap: 14px;
}

.toolbar-kicker {
  margin: 0 0 6px;
  color: var(--pf-gold-deep);
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 0.78rem;
}

.result-count {
  margin: 0;
  color: var(--pf-text);
}

.sort-control {
  display: grid;
  gap: 8px;
  color: var(--pf-text);
  font-size: 0.92rem;
}

.sort-control span {
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 0.74rem;
  color: var(--pf-muted);
}

.sort-control select {
  border: 1px solid #cfc6ba;
  background: rgba(255, 255, 255, 0.88);
  border-radius: 12px;
  padding: 10px 14px;
  min-width: 260px;
  color: var(--pf-navy);
  font-size: 0.95rem;
}

.acquisition-grid {
  max-width: 1280px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 28px;
}

.pagination-wrap {
  max-width: 1280px;
  margin: 32px auto 0;
  display: flex;
  justify-content: center;
}

.acquisition-card {
  background: var(--pf-card);
  border: 1px solid var(--pf-line);
  border-radius: 6px;
  overflow: hidden;
  transition: box-shadow 0.25s ease, transform 0.25s ease;
}

.acquisition-card:hover {
  box-shadow: 0 14px 30px rgba(31, 23, 16, 0.08);
  transform: translateY(-2px);
}

.card-image-wrap {
  position: relative;
  aspect-ratio: 4 / 3;
  background: var(--pf-soft);
  overflow: hidden;
}

.card-image-wrap img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.sold-tag {
  position: absolute;
  top: 12px;
  left: 12px;
  padding: 4px 12px;
  border-radius: 2px;
  background: var(--pf-dark);
  color: #fff;
  font-size: 0.72rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-weight: 600;
}

.card-main {
  padding: 20px 22px 22px;
}

.card-location {
  margin: 0 0 14px;
  color: var(--pf-dark);
  font-size: 1.05rem;
  font-weight: 600;
  letter-spacing: 0.01em;
}

.info-list {
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  padding: 9px 0;
  border-bottom: 1px solid rgba(221, 214, 205, 0.55);
}

.info-row:last-child {
  border-bottom: none;
}

.info-row dt {
  margin: 0;
  color: var(--pf-muted);
  font-size: 0.82rem;
  letter-spacing: 0.01em;
}

.info-row dd {
  margin: 0;
  color: var(--pf-dark);
  font-size: 0.92rem;
  font-weight: 500;
  text-align: right;
}

.info-row dd.gold {
  color: var(--pf-gold-deep);
  font-weight: 600;
}

.info-row-emphasis dd {
  color: var(--pf-gold-deep);
  font-weight: 600;
  font-size: 1rem;
}

.growth-disclaimer {
  max-width: 1280px;
  margin: 16px auto 0;
  color: var(--pf-muted);
  line-height: 1.7;
}

@media (max-width: 1200px) {

  .numbers-shell {
    grid-template-columns: 1fr;
  }

  .acquisition-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .container {
    padding: 94px 4% 64px;
  }

  .portfolio-hero {
    padding: 52px 0 28px;
  }

  .numbers-shell {
    padding: 18px;
  }

  .numbers-grid {
    grid-template-columns: 1fr;
  }

  .acquisition-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .portfolio-toolbar {
    display: grid;
    grid-template-columns: 1fr;
  }

  .sort-control {
    width: 100%;
  }

  .sort-control select {
    min-width: 0;
    width: 100%;
  }
}
</style>
